import { plural } from '@lingui/core/macro';
import { PermissionType } from '@watershed/constants/permissions';

import {
  GQMeasurementTaskForMeasurementPageFragment,
  GQNextMeasurementProjectDataFragment,
} from '@watershed/app-dashboard/generated/graphql-operations';
import { GQTaskWatershedProcessStateSimplified } from '@watershed/app-dashboard/generated/graphql-schema-types';

import { Trans, useLingui } from '@lingui/react/macro';
import { LinearProgress, Stack, Typography, Card } from '@mui/material';
import gql from 'graphql-tag';
import invariant from 'invariant';
import { useUserContext } from '../../utils/UserContext';

import { routeForMeasurementProject } from '@watershed/shared-universal/dashboardRoutes';
import IndexList from '@watershed/ui-core/components/IndexList';
import Avatar from '@watershed/ui-core/components/Avatar';
import HomeCardBase from './HomeCardBase';
import useHasPermission from '../../utils/useHasPermission';
import partition from 'lodash/partition';
import { getMeasurementHeader } from '@watershed/shared-universal/measurement/timelineHelpers';
import SetSquareIcon from '@watershed/icons/components/SetSquare';

import { formatDate } from '@watershed/intl/formatters';
import useLocale from '@watershed/intl/frontend/useLocale';

gql`
  fragment NextMeasurementProjectData on MeasurementProject {
    id
    name
    active
    coverageStartDate
    footprintStatus
    measurementTimeline {
      ...MeasurementTimelineForMeasurementPage
    }
    datasetsWithStages {
      ...DatasetWithStageForMeasurementPage
    }
    measurementTasks {
      ...MeasurementTaskForMeasurementPage
    }
  }
`;

function MeasurementTaskList({
  measurementTasks,
}: {
  measurementTasks: Array<GQMeasurementTaskForMeasurementPageFragment>;
}) {
  const numMeasurementTasks = measurementTasks.length;
  invariant(numMeasurementTasks > 0, 'zero tasks');
  const firstTaskTitle = measurementTasks.at(0)?.title;
  const secondTaskTitle = measurementTasks.at(1)?.title;

  // the plural macro renders the message for each case at runtime
  // we need to make sure extracting the task titles at possibly
  // non-existent indices does not cause a runtime error
  const taskList = plural(numMeasurementTasks, {
    one: `the ${firstTaskTitle} task`,
    2: `${firstTaskTitle} and ${secondTaskTitle} tasks`,
    3: `the ${firstTaskTitle}, ${secondTaskTitle}, and ${
      measurementTasks.at(2)?.title
    } tasks`,
    other: `the ${firstTaskTitle}, ${secondTaskTitle}, and ${
      measurementTasks.length - 2
    } other tasks`,
  });
  return <strong>{taskList}</strong>;
}

export default function NextMeasurementCard({
  project,
}: {
  project?: GQNextMeasurementProjectDataFragment | null;
}) {
  const { t } = useLingui();
  const locale = useLocale();

  const { userId, userName, orgName } = useUserContext();

  const hasManageMeasurement = useHasPermission([
    PermissionType.ManageMeasurement,
  ]);

  if (!project) {
    return null;
  }
  const measurementTimeline = project.measurementTimeline;
  if (!measurementTimeline) {
    return null;
  }

  const measurementTasks = project.measurementTasks;

  const openTasks = measurementTasks.filter((t) => {
    if (t.isAssignedToWatershed) {
      return false;
    }
    const closedTaskStatuses: Array<GQTaskWatershedProcessStateSimplified> = [
      GQTaskWatershedProcessStateSimplified.InProgress,
      GQTaskWatershedProcessStateSimplified.NeedsApproval,
      GQTaskWatershedProcessStateSimplified.Completed,
    ];
    return !closedTaskStatuses.includes(t.statusSimplified);
  });

  const [openTasksForUser, openTasksForOthers] = partition(openTasks, (t) =>
    t.assignees.map(({ id }) => id).includes(userId)
  );

  const unassignedIncompleteTasks = openTasks.filter(
    (t) => t.assignees.length === 0
  );
  const todos = [];

  if (openTasksForUser.length > 0) {
    todos.push(
      <Stack gap={1} direction="row">
        <Avatar variant="person" name={userName} />
        <span>
          <Trans comment="Referring to a list of open tasks">
            <strong>You </strong>
            <Typography variant="body2" component="span">
              need to complete{' '}
            </Typography>
          </Trans>
          <MeasurementTaskList measurementTasks={openTasksForUser} />
        </span>
      </Stack>
    );
  }

  if (hasManageMeasurement && openTasksForOthers.length > 0) {
    todos.push(
      <Stack gap={1} direction="row">
        <Avatar variant="person" name={orgName} />
        <span>
          <Trans comment="Referring to a list of open tasks">
            <strong>{orgName} </strong>
            <Typography variant="body2" component="span">
              needs to complete{' '}
            </Typography>
          </Trans>
          <MeasurementTaskList measurementTasks={openTasksForOthers} />
        </span>
      </Stack>
    );
  }

  if (hasManageMeasurement && unassignedIncompleteTasks.length > 0) {
    todos.push(
      <Stack gap={1} direction="row">
        <Avatar variant="person" name={userName} />
        <span>
          <Trans comment="Referring to a list of open tasks">
            <strong>You </strong>
            <Typography variant="body2" component="span">
              need to assign someone to complete{' '}
            </Typography>
          </Trans>
          <MeasurementTaskList measurementTasks={unassignedIncompleteTasks} />
        </span>
      </Stack>
    );
  }

  if (todos.length === 0) {
    if (hasManageMeasurement) {
      todos.push(
        <Trans>
          Your measurement is in progress! You'll receive an email a couple days
          before your measurement deadline if there are any remaining incomplete
          uploads.
        </Trans>
      );
    } else {
      todos.push(
        <Trans comment="Shown when the user has no tasks to complete">
          You've done your part! Once the measurement is complete we'll let you
          know.
        </Trans>
      );
    }
  }

  // For multiple-footprints, we disable anything footprint related in the card
  const totalDatasets = project.datasetsWithStages;
  const completedDatasets = totalDatasets.filter(
    (ea) => ea.areAllTasksComplete
  );

  const numCompletedDatasets = completedDatasets.length;
  const numTotalDatasets = totalDatasets.length;
  const projectDeadlineDate = project.measurementTimeline
    ? formatDate(project.measurementTimeline?.deadline, {
        locale,
        weekday: 'short',
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      })
    : undefined;

  const projectStatus = getMeasurementHeader(
    { ...project, measurementTimeline },
    locale
  );
  return (
    <HomeCardBase
      title={{ icon: SetSquareIcon, label: t`Measurement` }}
      link={{
        label: t`View tasks`,
        event: 'homeNextMeasurementCardButton',
        to: routeForMeasurementProject(project.id),
      }}
    >
      <Stack gap={2} p={2}>
        <Card
          sx={{
            display: 'flex',
            flexDirection: 'column',
            p: 2,
            bgcolor: (theme) => theme.palette.white,
            borderRadius: 1,
            border: (theme) => `1px solid ${theme.palette.grey20}`,
            gap: 1,
            width: '100%',
            boxShadow: '0px 1px 0.5px 0px rgba(79, 89, 110, 0.08)',
          }}
        >
          <Typography variant="h4">
            <Trans comment="Refers to the completion rate of the project">
              Progress
            </Trans>
          </Typography>
          <Typography variant="h4" fontWeight={500}>
            <Trans>
              {numCompletedDatasets} of {numTotalDatasets} datasets are complete
            </Trans>
          </Typography>
          <LinearProgress
            sx={{
              borderRadius: 1,
              height: '8px',
              bgcolor: (theme) => theme.palette.grey20,
              '> span': {
                bgcolor: (theme) => theme.palette.grass,
                borderRadius: 1,
              },
            }}
            variant="determinate"
            value={(numCompletedDatasets / numTotalDatasets) * 100}
          />
          <Stack
            justifyContent="space-between"
            direction="row"
            alignItems="center"
            height="24px"
          >
            <Typography variant="body3">
              <Trans>Status: {projectStatus}</Trans>
            </Typography>
            <Typography variant="body3">
              <Trans>Deadline: {projectDeadlineDate}</Trans>
            </Typography>
          </Stack>
        </Card>
        {todos.length > 0 && (
          <IndexList
            items={[
              <Typography key="title" variant="h4" sx={{ my: -1 }}>
                <Trans comment="Refers to a list of open tasks in the measurement project">
                  To-do's
                </Trans>
              </Typography>,
              ...todos,
            ].map((children, id) => ({ children, id }))}
          />
        )}
      </Stack>
    </HomeCardBase>
  );
}
